import { Alert, App, Form, Input, Modal, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { setUserPassword, type PanelUser } from './identityApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface PasswordModalProps {
  open: boolean
  user: PanelUser | null
  /** Amal shu paytda kirgan odamning O'ZIGA tegishlimi — ogohlantirish uchun */
  isSelf: boolean
  onClose: () => void
}

interface FormValues {
  password: string
  repeat: string
}

/**
 * Boshqa odamning parolini o'rnatish.
 *
 * ⚠️ JORIY PAROL KO'RSATILMAYDI va so'ralmaydi ham: server uni qaytarmaydi
 * (`PasswordRequest` — faqat yozish, CLAUDE.md §7). Bu forma faqat YANGI
 * qiymat o'rnatadi.
 *
 * ⚠️ Ikki marta kiritish MAJBURIY. Sabab: parol ekranda ko'rinmaydi va
 * hech qayerda qayta o'qib bo'lmaydi — xato yozilgan bo'lsa, buni faqat
 * odam kira olmaganda bilib qolinardi.
 *
 * ⚠️ Parol butun HISOBGA tegishli: odam bir nechta tashkilotda ishlasa,
 * bu amal ularning hammasidagi kirishiga ta'sir qiladi. Shuning uchun
 * modalda bu aniq yozilgan.
 */
export function PasswordModal({ open, user, isSelf, onClose }: PasswordModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()

  const save = useMutation({
    mutationFn: (values: FormValues) => setUserPassword(user?.id ?? '', values.password),
    onSuccess: () => {
      void message.success(t('identity.password.done'))
      form.resetFields()
      onClose()
    },
  })

  return (
    <Modal
      open={open}
      title={t('identity.password.title')}
      onCancel={() => {
        save.reset()
        form.resetFields()
        onClose()
      }}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Typography.Paragraph>
        <Typography.Text strong>{user?.username}</Typography.Text>
      </Typography.Paragraph>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={isSelf ? t('identity.password.selfWarning') : t('identity.password.scopeWarning')}
      />
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="password"
          label={t('identity.password.field')}
          extra={t('identity.create.passwordHint')}
          rules={[
            { required: true, message: t('identity.create.passwordRequired') },
            { min: 12, max: 128, message: t('identity.create.passwordLength') },
          ]}
        >
          <Input.Password visibilityToggle={false} autoFocus autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="repeat"
          label={t('identity.password.repeat')}
          dependencies={['password']}
          rules={[
            { required: true, message: t('identity.password.repeatRequired') },
            // antd validator'i `unknown` beradi — `no-unsafe-*` qoidalari
            // uchun aniq tiplaymiz, aks holda tip xavfsizligi shu yerda
            // jimgina yo'qolardi
            ({ getFieldValue }) => ({
              validator: (_rule, value: unknown) => {
                const first: unknown = getFieldValue('password')
                if (typeof value === 'string' && value === first) return Promise.resolve()
                return Promise.reject(new Error(t('identity.password.mismatch')))
              },
            }),
          ]}
        >
          <Input.Password visibilityToggle={false} autoComplete="new-password" />
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">{t('identity.password.notShown')}</Typography.Text>
    </Modal>
  )
}
